/**
 * Client-side brand kits. Mirrors api/_lib/brandKits.js presets so chrome
 * can theme immediately from the session payload without an extra fetch.
 */

export const IPM_KIT = {
    id: 'ipm',
    name: 'International Property Market',
    productName: 'IPM',
    tagline: 'The International Property Market',
    website: 'https://www.internationalpropertymarket.com',
    hideIpmBranding: false,
    logos: {
        wordmark: '/logo-white.png',
        stacked: '/logo-white.png',
        mark: '/logo-white.png',
        onDark: '/logo-white.png',
        onLight: '/logo-white.png',
        favicon: '/favicon.ico',
    },
    colors: {
        primary: '#10575C',
        secondary: '#1F3A3D',
        accent: '#FFB21B',
        cta: '#FFB21B',
        background: '#F8FAFC',
        surface: '#FFFFFF',
        border: '#E2E8F0',
        text: '#1F2937',
        muted: '#64748B',
        onPrimary: '#FFFFFF',
        sage: '#E8F2F3',
        chrome: '#10575C',
        onChrome: '#FFFFFF',
    },
    typography: {
        fontFamily: 'Poppins',
        googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap',
    },
    contact: { email: '', phone: '', whatsapp: '' },
};

export const MZANSI_BONDS_KIT = {
    id: 'mzansi-bonds',
    name: 'Mzansi Bonds',
    productName: 'Mzansi Bonds',
    tagline: 'Own Your Dream',
    website: 'https://www.mzansibonds.co.za/',
    hideIpmBranding: true,
    logos: {
        wordmark: '/brand-kits/mzansi-bonds/logo.png',
        stacked: '/brand-kits/mzansi-bonds/logo-stacked.png',
        mark: '/brand-kits/mzansi-bonds/mark.png',
        onDark: '/brand-kits/mzansi-bonds/logo.png',
        onLight: '/brand-kits/mzansi-bonds/logo.png',
        favicon: '/brand-kits/mzansi-bonds/favicon.png',
    },
    colors: {
        // Site “As easy as…” section: cream field, mint discs, orange CTAs.
        // Forest green is type, not a chrome fill.
        primary: '#004236',
        secondary: '#1F6B5A',
        accent: '#F15A24',
        cta: '#FF4500',
        background: '#CCE4D6',
        surface: '#FFFFFF',
        border: '#E4E0D0',
        text: '#004236',
        muted: '#5C7A72',
        onPrimary: '#FFFFFF',
        sage: '#BDE3D3',
        chrome: '#F5F5ED',
        onChrome: '#004236',
    },
    typography: {
        fontFamily: 'Outfit',
        googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap',
    },
    contact: {
        email: 'hello@mzansibonds.co.za',
        phone: '082 770 4275',
        whatsapp: '27827704275',
    },
};

export const TYSON_KIT = {
    id: 'tyson',
    name: 'Tyson Properties',
    productName: 'Tyson Properties',
    tagline: 'Expect more.',
    website: 'https://www.tysonprop.co.za/',
    hideIpmBranding: true,
    logos: {
        wordmark: '/agencies/tyson-logo.png',
        stacked: '/agencies/tyson-logo.png',
        mark: '/agencies/tyson-logo.png',
        onDark: '/agencies/tyson-logo.png',
        onLight: '/agencies/tyson-logo.png',
        favicon: '/agencies/tyson-logo.png',
    },
    colors: {
        // Dark Teal Pantone 309C. Lime sampled from Tyson’s RENT badge (not IPM gold).
        primary: '#004c50',
        secondary: '#0F2A2C',
        accent: '#83BD55',
        cta: '#83BD55',
        background: '#F8FAFC',
        surface: '#FFFFFF',
        border: '#D5DEDC',
        text: '#0F2A2C',
        muted: '#5A6B6C',
        onPrimary: '#FFFFFF',
        sage: '#E8F4DC',
        chrome: '#004c50',
        onChrome: '#FFFFFF',
    },
    typography: {
        fontFamily: 'Barlow',
        googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Barlow:wght@400;500;600;700;800&display=swap',
    },
    contact: {
        email: '',
        phone: '',
        whatsapp: '',
    },
};

export const PRESETS = {
    'mzansi-bonds': MZANSI_BONDS_KIT,
    tyson: TYSON_KIT,
};

const VAR_KEYS = [
    '--brand-primary', '--brand-secondary', '--brand-accent', '--brand-cta',
    '--brand-bg', '--brand-surface', '--brand-border', '--brand-text', '--brand-muted',
    '--brand-on-primary', '--brand-sage', '--brand-font',
    '--brand-chrome', '--brand-on-chrome',
    '--ipm-green', '--ipm-green-dark', '--ipm-orange', '--ipm-yellow',
    '--brand-chrome-from', '--brand-chrome-mid', '--brand-chrome-to',
];

function hexToRgb(hexColor) {
    let h = String(hexColor || '').replace('#', '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length < 6) return { r: 16, g: 87, b: 92 };
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
}

export function isLightColor(hexColor) {
    const { r, g, b } = hexToRgb(hexColor);
    return (r * 299 + g * 587 + b * 114) / 1000 > 155;
}

export function cssVarsFromKit(kit) {
    if (!kit) return {};
    const chrome = kit.colors.chrome || kit.colors.primary;
    const onChrome = kit.colors.onChrome || kit.colors.onPrimary || '#FFFFFF';
    const { r, g, b } = hexToRgb(chrome);
    const light = isLightColor(chrome);
    const font = kit.typography?.fontFamily || 'Poppins';
    return {
        '--brand-primary': kit.colors.primary,
        '--brand-secondary': kit.colors.secondary,
        '--brand-accent': kit.colors.accent,
        '--brand-cta': kit.colors.cta,
        '--brand-bg': kit.colors.background,
        '--brand-surface': kit.colors.surface,
        '--brand-border': kit.colors.border,
        '--brand-text': kit.colors.text,
        '--brand-muted': kit.colors.muted,
        '--brand-on-primary': kit.colors.onPrimary,
        '--brand-sage': kit.colors.sage,
        '--brand-chrome': chrome,
        '--brand-on-chrome': onChrome,
        '--brand-font': `'${font}', 'Poppins', sans-serif`,
        '--ipm-green': kit.colors.primary,
        '--ipm-green-dark': kit.colors.primary,
        '--ipm-orange': kit.colors.accent,
        '--ipm-yellow': kit.colors.cta,
        '--brand-chrome-from': light ? chrome : `rgba(${r},${g},${b},0.92)`,
        '--brand-chrome-mid': light ? chrome : `rgba(${Math.max(0, r - 8)},${Math.max(0, g - 8)},${Math.max(0, b - 8)},0.94)`,
        '--brand-chrome-to': light ? chrome : `rgba(${Math.max(0, r - 20)},${Math.max(0, g - 20)},${Math.max(0, b - 20)},0.96)`,
    };
}

function brandHintFromUser(user) {
    const kit = String(user?.whiteLabel?.kitId || user?.brandKit?.id || '').toLowerCase();
    if (kit === 'tyson') return 'tyson';
    if (kit === 'mzansi-bonds' || kit === 'mzansi') return 'mzansi-bonds';
    if (user?.brand === 'tyson') return 'tyson';
    // Org label first. "Mzansi Structures - Chris Tyson" contains both words.
    // The surname must not steal the Mzansi kit.
    const org = String(user?.agencyName || '').toLowerCase();
    if (org.includes('mzansi')) return 'mzansi-bonds';
    if (org.includes('tyson')) return 'tyson';
    const hay = `${user?.name || ''} ${user?.email || ''}`.toLowerCase();
    if (hay.includes('tyson')) return 'tyson';
    if (hay.includes('mzansi')) return 'mzansi-bonds';
    return null;
}

function kitFromHint(hint) {
    if (hint === 'tyson') return TYSON_KIT;
    if (hint === 'mzansi-bonds') return MZANSI_BONDS_KIT;
    return null;
}

export function resolveKitFromUser(user) {
    if (!user) return null;
    if (user.brandKit && user.whiteLabel?.enabled) return user.brandKit;
    const wl = user.whiteLabel;
    if (wl && wl.enabled) {
        const kitId = wl.kitId || '';
        if (kitId && kitId !== 'custom' && PRESETS[kitId]) {
            const overlay = wl.kit && typeof wl.kit === 'object' ? wl.kit : {};
            return {
                ...PRESETS[kitId],
                ...overlay,
                colors: { ...PRESETS[kitId].colors, ...(overlay.colors || {}) },
                logos: { ...PRESETS[kitId].logos, ...(overlay.logos || {}) },
                typography: { ...PRESETS[kitId].typography, ...(overlay.typography || {}) },
            };
        }
        if (wl.kit && typeof wl.kit === 'object' && (wl.kit.logos || wl.kit.colors)) return wl.kit;
        if (PRESETS[kitId]) return PRESETS[kitId];
    }
    // Empty agencyId / stored enabled:false still inherit Tyson or Mzansi from name/email.
    return kitFromHint(brandHintFromUser(user));
}

export function pickLogo(kit, variant = 'onDark') {
    if (!kit || !kit.logos) return '/logo-white.png';
    const logos = kit.logos;
    if (variant === 'onLight') return logos.onLight || logos.wordmark || logos.stacked || logos.mark || '/logo-white.png';
    if (variant === 'mark') return logos.mark || logos.favicon || logos.wordmark || '/logo-white.png';
    if (variant === 'stacked') return logos.stacked || logos.wordmark || '/logo-white.png';
    return logos.onDark || logos.wordmark || logos.mark || '/logo-white.png';
}

const FONT_LINK_ID = 'ipm-brandkit-font';
const FAVICON_ATTR = 'data-ipm-brandkit';

export function applyBrandKitToDocument(kit) {
    const root = document.documentElement;
    if (!kit) {
        root.removeAttribute('data-whitelabel');
        root.removeAttribute('data-chrome');
        root.removeAttribute('data-hide-ipm');
        VAR_KEYS.forEach((k) => root.style.removeProperty(k));
        const font = document.getElementById(FONT_LINK_ID);
        if (font) font.remove();
        restoreFavicon();
        return;
    }
    root.setAttribute('data-whitelabel', 'true');
    const chrome = kit.colors?.chrome || kit.colors?.primary;
    root.setAttribute('data-chrome', isLightColor(chrome) ? 'light' : 'dark');
    if (kit.hideIpmBranding) root.setAttribute('data-hide-ipm', 'true');
    else root.removeAttribute('data-hide-ipm');
    const vars = cssVarsFromKit(kit);
    Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
    if (kit.typography?.googleFontsUrl) {
        let link = document.getElementById(FONT_LINK_ID);
        if (!link) {
            link = document.createElement('link');
            link.id = FONT_LINK_ID;
            link.rel = 'stylesheet';
            document.head.appendChild(link);
        }
        link.href = kit.typography.googleFontsUrl;
    }
    setFavicon(kit.logos?.favicon || kit.logos?.mark);
}

function setFavicon(href) {
    if (!href) return;
    const links = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
    if (!links.length) {
        const link = document.createElement('link');
        link.rel = 'icon';
        link.setAttribute(FAVICON_ATTR, '1');
        link.href = href;
        document.head.appendChild(link);
        return;
    }
    links.forEach((link) => {
        if (!link.getAttribute(FAVICON_ATTR)) {
            link.setAttribute(`${FAVICON_ATTR}-prev`, link.getAttribute('href') || '');
        }
        link.setAttribute(FAVICON_ATTR, '1');
        link.href = href;
    });
}

function restoreFavicon() {
    document.querySelectorAll(`link[${FAVICON_ATTR}]`).forEach((link) => {
        const prev = link.getAttribute(`${FAVICON_ATTR}-prev`);
        if (prev) link.href = prev;
        else if (link.getAttribute(FAVICON_ATTR) === '1' && !link.getAttribute(`${FAVICON_ATTR}-prev`)) {
            link.href = '/favicon.ico';
        }
        link.removeAttribute(FAVICON_ATTR);
        link.removeAttribute(`${FAVICON_ATTR}-prev`);
    });
}

export const SESSION_UPDATED_EVENT = 'ipm:session-updated';

export function notifySessionUpdated() {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new Event(SESSION_UPDATED_EVENT));
}
